import i18next from 'i18next'
import { registerTaskType } from './task-type-registry'
import { taskTypeEnum, AsyncJob } from './models'

registerTaskType(
  taskTypeEnum.TRA_SUBMISSION,
  (job: AsyncJob) => {
    const reservationId = job.entityId
    const label = job.label || i18next.t('asyncJobs.traSubmission')

    if (job.status === 'done') {
      return {
        message: label,
        href: `/reservations/${reservationId}`,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${label}${errorMsg}`,
        href: `/reservations/${reservationId}`,
      }
    }

    return { message: label }
  },
  i18next.t('asyncJobs.traSubmission'),
  (job) => [['reservations'], ['reservations', job.entityId]],
)

registerTaskType(
  taskTypeEnum.SUBMISSION_RECEIPT,
  (job: AsyncJob) => {
    const label = job.label || i18next.t('asyncJobs.submissionReceipt')
    const errorMsg = job.status === 'failed' && job.errorMessage ? `: ${job.errorMessage}` : ''
    return { message: `${label}${errorMsg}` }
  },
  i18next.t('asyncJobs.submissionReceipt'),
  (job) => [['reservations'], ['reservations', job.entityId]],
)

registerTaskType(
  taskTypeEnum.STATEMENT_PROCESSING,
  (job: AsyncJob) => {
    const statementId = job.entityId
    const label = job.label || i18next.t('asyncJobs.statementProcessing')
    const reviewHref = `/expenses/statements/${statementId}/review`

    if (job.status === 'done') {
      return {
        message: i18next.t('asyncJobs.statementReadyForReview'),
        href: reviewHref,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${label}${errorMsg}`,
        href: reviewHref,
      }
    }

    return { message: label }
  },
  i18next.t('asyncJobs.statementProcessing'),
  (job) => [['statements'], ['statements', job.entityId]],
)

registerTaskType(
  taskTypeEnum.STATEMENT_RECONCILIATION,
  (job: AsyncJob) => {
    const statementId = job.entityId
    const label = job.label || i18next.t('asyncJobs.statementReconciliation')
    const reviewHref = `/expenses/statements/${statementId}/review`

    if (job.status === 'done') {
      return {
        message: i18next.t('asyncJobs.statementReadyForReview'),
        href: reviewHref,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${label}${errorMsg}`,
        href: reviewHref,
      }
    }

    return { message: label }
  },
  i18next.t('asyncJobs.statementReconciliation'),
  (job) => [['statements'], ['statements', job.entityId], ['statements', job.entityId, 'rows']],
)

registerTaskType(
  taskTypeEnum.GENERATE_QUERY_VECTOR,
  (job: AsyncJob) => {
    if (job.status === 'done') {
      return {
        message: i18next.t('asyncJobs.semanticSearchReady'),
        suppressToast: true,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${i18next.t('asyncJobs.generatingSearchEmbedding')}${errorMsg}`,
      }
    }

    return { message: i18next.t('asyncJobs.generatingSearchEmbedding') }
  },
  'Search Embedding',
  () => [['expenses']],
)

registerTaskType(
  taskTypeEnum.GENERATE_FILTERS,
  (job: AsyncJob) => {
    if (job.status === 'done') {
      return {
        message: i18next.t('asyncJobs.searchFiltersReady'),
        suppressToast: true,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${i18next.t('asyncJobs.generatingFilters')}${errorMsg}`,
      }
    }

    return { message: i18next.t('asyncJobs.generatingFilters') }
  },
  'Search Filters',
  () => [['expenses']],
)

registerTaskType(
  taskTypeEnum.RECEIPT_ANALYSIS,
  (job: AsyncJob) => {
    if (job.status === 'done') {
      // The analysis result lands in the expense form on its own, so a success
      // toast is noise. Failures still toast — the user has to know.
      return {
        message: i18next.t('asyncJobs.receiptAnalyzed'),
        suppressToast: true,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${i18next.t('asyncJobs.receiptAnalysisFailed')}${errorMsg}`,
      }
    }

    return { message: job.label || i18next.t('asyncJobs.analyzingReceipt') }
  },
  'Receipt Analysis',
  (job) => [['receipts'], ['receipts', job.entityId]],
)

registerTaskType(
  taskTypeEnum.CUENTA_DE_COBRO_GENERATION,
  (job: AsyncJob) => {
    const expenseId = job.entityId

    if (job.status === 'done') {
      return {
        message: i18next.t('asyncJobs.cuentaDeCobroGenerated'),
        href: `/expenses/${expenseId}`,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${i18next.t('asyncJobs.cuentaDeCobroFailed')}${errorMsg}`,
        href: `/expenses/${expenseId}`,
      }
    }

    return { message: job.label || i18next.t('asyncJobs.generatingCuentaDeCobro') }
  },
  'Cuenta de Cobro',
  (job) => [['expenses'], ['expenses', job.entityId]],
)

registerTaskType(
  taskTypeEnum.BULK_OPERATION,
  (job: AsyncJob) => {
    // Every bulk action (delete, categorize, add-to-report, download) shares this
    // task type. The per-action label set server-side carries the specifics, so
    // the message is label-driven with a generic fallback.
    if (job.status === 'done') {
      // Bulk-download exposes a download link in its result; surface it as the
      // toast's href so the finished archive is one click away. Other bulk
      // actions carry no link and fall through to a plain message.
      const downloadUrl = job.result?.downloadUrl as string | undefined
      return {
        message: job.label || i18next.t('asyncJobs.bulkOperationDone'),
        ...(downloadUrl ? { href: downloadUrl } : {}),
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return { message: `${job.label || i18next.t('asyncJobs.bulkOperationFailed')}${errorMsg}` }
    }

    return { message: job.label || i18next.t('asyncJobs.bulkOperation') }
  },
  i18next.t('asyncJobs.bulkOperation'),
  () => [['expenses']],
)

registerTaskType(
  taskTypeEnum.EXPORT_REPORT,
  (job: AsyncJob) => {
    const reportId = job.entityId

    if (job.status === 'done') {
      return {
        message: i18next.t('asyncJobs.reportReady'),
        href: `/expense-reports/report/${reportId}`,
      }
    }

    if (job.status === 'failed') {
      const errorMsg = job.errorMessage ? `: ${job.errorMessage}` : ''
      return {
        message: `${i18next.t('asyncJobs.reportGenerationFailed')}${errorMsg}`,
        href: `/expense-reports/report/${reportId}`,
      }
    }

    return {
      message: job.label || i18next.t('asyncJobs.generatingReport'),
      href: `/expense-reports/report/${reportId}`,
    }
  },
  'Report Export',
  (job) => [['expense-reports'], ['expense-reports', job.entityId]],
)
